import React, { useState, useEffect, useRef } from "react";

const EMOJIS = ["🧘", "🌸", "🌊", "🌲", "☀️", "🌕", "🕊️", "🦋", "🍂", "🏔️", "☯️", "🎐"];

const shuffle = (arr) => {
    let i = arr.length, r;
    while (i) {
        r = Math.floor(Math.random() * i--);
        [arr[i], arr[r]] = [arr[r], arr[i]];
    }
    return arr;
};

function Card({ card, onCardClick }) {
    return (
        <div
            className={`card ${card.isFlipped ? "flipped" : ""} ${card.isMatched ? "matched" : ""}`}
            onClick={() => !card.isFlipped && onCardClick(card)}
        >
            <div className="front">{card.emoji}</div>
            <div className="back"></div>
        </div>
    );
}

const StyleInjector = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;500;700&display=swap');

    html, body {
      height: 100%;
      margin: 0;
      font-family: 'Poppins', sans-serif;
      background: linear-gradient(115deg, #6fb1fc, #3d95ff, #52d6ff);
      background-size: 250% 250%;
      animation: bgFlow 10s ease infinite;
    }

    @keyframes bgFlow {
      0% {background-position: 0% 50%;}
      50% {background-position: 100% 50%;}
      100% {background-position: 0% 50%;}
    }

    /* ✅ Allow internal scrolling but keep zen full-screen look */
    .zen-memory-bg {
      min-height: 100vh;
      width: 100vw;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      overflow-y: auto;
      padding-bottom: 40px; /* ✅ Prevent bottom cutoff */
    }

    .modal-container {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.35);
      backdrop-filter: blur(12px);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 50;
    }

    .glass-card {
      padding: 35px;
      width: 330px;
      background: rgba(255,255,255,0.3);
      border: 2px solid rgba(255,255,255,0.7);
      border-radius: 18px;
      text-align: center;
      box-shadow: 0 0 25px rgba(0,0,0,0.25);
    }

    .start-btn {
      padding: 12px;
      border-radius: 30px;
      width: 100%;
      font-weight: 700;
      font-size: 16px;
      background: #004be7;
      color: white;
      border: none;
      cursor: pointer;
      transition: .25s;
    }
    .start-btn:hover { background: #003bb9; transform: scale(1.03); }

    .aura-ring {
      width: 250px;
      height: 250px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(255,255,255,0.35), transparent 70%);
      position: absolute;
      top: 70px;
      animation: pulseAura 4s ease-in-out infinite;
      filter: blur(15px);
      z-index: 0;
    }

    @keyframes pulseAura {
      0%,100% {transform: scale(1);}
      50% {transform: scale(1.2);}
    }

    .big-zen-title {
      margin-top: 60px;
      font-size: 48px;
      font-weight: 800;
      text-align: center;
      background: linear-gradient(90deg,#ffffff,#dff6ff,#ffffff);
      -webkit-background-clip: text;
      color: transparent;
      text-shadow: 0 0 20px rgba(255,255,255,0.6);
      z-index: 5;
    }

    .sub-focus {
      font-size: 18px;
      color: white;
      opacity: .9;
      margin-top: 4px;
      margin-bottom: 10px;
      z-index: 5;
    }

    .game-board-wrap {
      padding: 25px;
      
      border-radius: 22px;
      background: rgba(85, 54, 54, 0.13);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.4);
      box-shadow: 0 8px 35px rgba(0,0,0,0.2);
      z-index: 5;
      margin-bottom: 60px; /* ✅ Adds breathing space */
      transform: translateY(-20px) scale(0.9);
    }

    #gameBoard {
      width: 630px;
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 10px;
    }

    .card {
      width: 88px;
      height: 108px;
      position: relative;
      transform-style: preserve-3d;
      transition: .45s;
      cursor: pointer;
    }

    .card.flipped { transform: rotateY(180deg); }
    .card.matched { box-shadow: 0 0 18px #00ffee; }

    .card div {
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 12px;
      backface-visibility: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 42px;
    }

    .front {
      background: white;
      border: 3px solid #064ece;
      transform: rotateY(180deg);
    }

    .back {
      background: linear-gradient(135deg, #2563eb, #4f83ff);
      border: 3px solid #ffffffaa;
    }

    .btn-row {
      display: flex;
      justify-content: center;
      gap: 14px;
      margin-top: 10px;
    }

    .btn {
      padding: 10px 20px;
      border-radius: 20px;
      font-weight: 600;
      color: #2563eb;
      background: white;
      border: none;
      cursor: pointer;
      transition: .2s;
    }
    .btn:hover { transform: scale(1.08); }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return null;
};


function ZenMemory() {
    const [deck, setDeck] = useState([]);
    const [flipped, setFlipped] = useState([]);
    const [moves, setMoves] = useState(0);
    const [matches, setMatches] = useState(0);
    const [lock, setLock] = useState(false);
    const [start, setStart] = useState(false);
    const [mute, setMute] = useState(false);
    const audioRef = useRef(null);

    const startGame = () => {
        const shuffled = shuffle([...EMOJIS, ...EMOJIS]);
        setDeck(shuffled.map((e, i) => ({ id: i, emoji: e, isFlipped: false, isMatched: false })));
        setMoves(0); setMatches(0); setFlipped([]); setLock(false);
    };

    const onStart = () => {
        setStart(true);
        audioRef.current.play().catch(() => { });
        startGame();
    };

    const flip = card => {
        if (lock || card.isFlipped) return;
        setDeck(d => d.map(c => c.id === card.id ? { ...c, isFlipped: true } : c));
        setFlipped(f => [...f, card]);
    };

    useEffect(() => {
        if (flipped.length === 2) {
            setMoves(m => m + 1);
            setLock(true);
            const [a, b] = flipped;
            if (a.emoji === b.emoji) {
                setMatches(m => m + 1);
                setDeck(d => d.map(c => c.emoji === a.emoji ? { ...c, isMatched: true } : c));
                setFlipped([]); setLock(false);
            } else {
                setTimeout(() => {
                    setDeck(d => d.map(c => (c.id === a.id || c.id === b.id) ? { ...c, isFlipped: false } : c));
                    setFlipped([]); setLock(false);
                }, 700);
            }
        }
    }, [flipped]);

    return (
        <>
            <StyleInjector />
            <audio ref={audioRef} src="/spring-flowers.mp3" loop />

            {!start && (
                <div className="modal-container">
                    <div className="glass-card">
                        <h2>🧘 Zen Memory Game</h2>
                        <p>Calm your mind & sharpen focus</p>
                        <button className="start-btn" onClick={onStart}>Start Game</button>
                    </div>
                </div>
            )}

            <div className="zen-memory-bg">
                {start && (
                    <>
                        <div className="aura-ring"></div>

                        <h1 className="big-zen-title">Zen Memory</h1>
                        <p className="sub-focus">Breathe · Focus · Remember</p>

                        <div className="game-board-wrap">
                            <p>Moves: {moves} | Matches: {matches}/{EMOJIS.length}</p>

                            <div id="gameBoard">
                                {deck.map(card => <Card key={card.id} card={card} onCardClick={flip} />)}
                            </div>

                            <div className="btn-row">
                                <button className="btn" onClick={startGame}>Restart</button>
                                <button className="btn"
                                    onClick={() => {
                                        mute ? audioRef.current.play() : audioRef.current.pause();
                                        setMute(!mute);
                                    }}
                                >
                                    {mute ? "🎵 Music" : "🔇 Mute"}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default ZenMemory;
